import giftCardIcon from '@/assets/images/gift-card-icon.png';
import { Button } from '@/components/ui/button';
import { CustomInput } from '@/components/ui/input';
import { showToast } from '@/components/ui/sonner';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CheckCircle2, Mail, Tag } from 'lucide-react';
import { useState } from 'react';
import { CREATE_GIFT_CARD_ACTIVATION_SESSION } from '../Graphql/mutations';
import { VALIDATE_GIFT_CARD } from '../Graphql/queries';
import type { ActivationState, PackageSummary } from '../index';

interface CodeEntryStepProps {
  initialCode?: string;
  onNext: (partial: Partial<ActivationState>) => void;
}

export default function CodeEntryStep({ initialCode = '', onNext }: CodeEntryStepProps) {
  const [code, setCode] = useState(initialCode);
  const [error, setError] = useState('');

  const [validateCard, { loading: validating }] = useLazyQuery(VALIDATE_GIFT_CARD, {
    fetchPolicy: 'network-only',
  });

  const [createSession, { loading: creating }] = useMutation(
    CREATE_GIFT_CARD_ACTIVATION_SESSION,
  );

  const loading = validating || creating;

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setError('Please enter a gift card code.');
      return;
    }
    setError('');

    try {
      const { data: validateData, error: validateError } = await validateCard({
        variables: { code: trimmed },
      });

      if (validateError) throw validateError;

      const validation = validateData?.validateGiftCardCodeForActivation;
      if (validation?.result !== 'PENDING_PAYMENT') {
        setError(validation?.message ?? 'Invalid gift card code. Please try again.');
        return;
      }

      const { data: sessionData, errors: sessionErrors } = await createSession({
        variables: { code: trimmed },
      });

      if (sessionErrors?.length) throw new Error(sessionErrors[0].message);

      onNext({
        code: trimmed,
        packageSummary: validation.packageSummary as PackageSummary,
        activationSessionToken:
          sessionData?.createGiftCardActivationSession?.activationSessionToken ?? '',
      });
    } catch (err: any) {
      showToast({ variant: 'error', message: err?.message ?? 'Something went wrong.' });
    }
  };

  return (
    <div className='flex flex-col items-center gap-8'>
      {/* Gift card icon */}
      <img
        src={giftCardIcon}
        alt='Gift Card'
        className='w-40 h-auto'
        draggable={false}
      />

      {/* Subtitle */}
      <h2 className='text-xl font-bold text-white text-center'>
        Enter Gift Card Code
      </h2>

      {/* Form */}
      <form onSubmit={handleVerify} className='w-full flex flex-col gap-6'>
        <div className='rounded-2xl bg-[#181A32] p-5'>
          <CustomInput
            id='gift-card-code'
            label='Gift Card Code*'
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (error) setError('');
            }}
            error={error}
            className='uppercase'
            autoFocus
          />
        </div>

        {/* Buttons */}
        <div className='flex gap-4'>
          <Button
            id='gift-code-back-btn'
            type='button'
            className='flex-1 h-12 font-bold text-black border-0'
            style={{
              background:
                'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
            }}
            onClick={() => window.history.back()}
          >
            Back
          </Button>
          <Button
            id='activate-gift-card-btn'
            type='submit'
            className='flex-1 h-12 font-bold text-black'
            style={{
              background:
                'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
            }}
            disabled={loading}
            loading={loading}
          >
            {loading ? 'Verifying…' : 'Verify Code'}
          </Button>
        </div>
      </form>

      {/* Guidance notes */}
      <div className='w-full rounded-2xl bg-[#181A32] p-5 flex flex-col gap-3'>
        <p className='text-xs font-semibold text-[#A8ADB9] uppercase tracking-wider'>
          Where to find your code
        </p>
        <div className='flex flex-col gap-3'>
          <div className='flex items-start gap-3'>
            <div className='mt-0.5 shrink-0 h-7 w-7 rounded-full bg-[#0A7AFF]/15 flex items-center justify-center'>
              <Mail className='h-3.5 w-3.5 text-[#0A7AFF]' />
            </div>
            <div>
              <p className='text-xs font-medium text-white'>Gift Email</p>
              <p className='text-[11px] text-[#A8ADB9] mt-0.5 leading-relaxed'>
                Check your inbox for a gift card email — the unique code is printed inside.
              </p>
            </div>
          </div>
          <div className='flex items-start gap-3'>
            <div className='mt-0.5 shrink-0 h-7 w-7 rounded-full bg-[#E9CC7C]/15 flex items-center justify-center'>
              <Tag className='h-3.5 w-3.5 text-[#E9CC7C]' />
            </div>
            <div>
              <p className='text-xs font-medium text-white'>Physical Card</p>
              <p className='text-[11px] text-[#A8ADB9] mt-0.5 leading-relaxed'>
                The code is printed directly on your physical card.
              </p>
            </div>
          </div>
          <div className='flex items-start gap-3'>
            <div className='mt-0.5 shrink-0 h-7 w-7 rounded-full bg-[#22c55e]/10 flex items-center justify-center'>
              <CheckCircle2 className='h-3.5 w-3.5 text-[#22c55e]' />
            </div>
            <div>
              <p className='text-xs font-medium text-white'>Code Format</p>
              <p className='text-[11px] text-[#A8ADB9] mt-0.5 leading-relaxed'>
                Codes look like{' '}
                <span className='font-mono text-white/80 tracking-wider'>FPXX-XXXXXXXXXX</span>
                . You can enter the code with or without spaces — any format works.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
